// StateRegulationsMap.jsx (Use version from previous answer - already simplified)

import React, { memo } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { stateRequirements } from '../data/stateRequirements';
import '../styles/StateRegulationsMap.css';

// Helper function (ensure this is correct and outside component)
const formatCurrencyThreshold = (value) => {
    if (value === null || typeof value === 'undefined') {
        return 'N/A';
    }
    if (typeof value === 'number') {
        return `$${value.toLocaleString()}`;
    }
    console.warn(`formatCurrencyThreshold expected a number but received ${typeof value}:`, value);
    return 'Invalid Data';
};


const MapChart = memo(({ selectedState, onStateClick }) => {
  const isClickHandlerValid = typeof onStateClick === 'function';

  return (
    <ComposableMap
      projection="geoAlbersUsa"
      projectionConfig={{ scale: 800, center: [0, 40] }}
      style={{ width: "100%", height: "auto" }}
    >
      <Geographies geography="/usa-states.json">
        {({ geographies }) =>
          geographies.map(geo => {
            const stateName = geo.properties.name;
            return (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                // Pass the validated handler directly
                onClick={isClickHandlerValid ? () => onStateClick(stateName) : undefined}
                style={{
                  default: { fill: stateName === selectedState ? "#059669" : "#1f2937", stroke: "#ffffff", strokeWidth: 0.5, outline: "none", cursor: isClickHandlerValid ? 'pointer' : 'default' },
                  hover: { fill: isClickHandlerValid ? "#065f46" : "#1f2937", stroke: "#ffffff", strokeWidth: 0.75, outline: "none" },
                  pressed: { fill: isClickHandlerValid ? "#047857" : "#1f2937", stroke: "#ffffff", strokeWidth: 0.75, outline: "none" }
                }}
              />
            );
          })
        }
      </Geographies>
    </ComposableMap>
  );
});
MapChart.displayName = 'MapChart';


const StateRegulationsMap = ({ onStateClick, selectedState }) => {

  // No intermediate handler needed here

  const getStateInfo = (stateName) => {
    if (!stateName) return null;
    return Object.values(stateRequirements).find(
      state => state.name === stateName
    );
  };

  const selectedStateInfo = getStateInfo(selectedState);

  return (
    <>
      <div className="map-container">
        <MapChart
          selectedState={selectedState}
          // Pass the prop directly from parent
          onStateClick={onStateClick}
        />
      </div>

      {/* State Info Panel (Ensure this JSX is correct) */}
      {selectedState && selectedStateInfo ? (
         <div className="state-info-panel">
           <h3>{selectedState} Requirements</h3>
           <div className="regulations-grid">
             <div className="regulation-section">
               <h4>Registration & Financials</h4>
               <ul>
                 <li>Initial Fee: {formatCurrencyThreshold(selectedStateInfo.initialFee)}</li>
                 <li>Renewal Fee: {formatCurrencyThreshold(selectedStateInfo.renewalFee)}</li>
                 <li>Audit Required Above: {formatCurrencyThreshold(selectedStateInfo.auditThreshold)}</li>
                 <li>Review Required Above: {formatCurrencyThreshold(selectedStateInfo.reviewThreshold)}</li>
                 {selectedStateInfo.hasOwnProperty('foreignEntityFee') && (
                   <li>Foreign Entity Fee: {formatCurrencyThreshold(selectedStateInfo.foreignEntityFee)}</li>
                 )}
               </ul>
             </div>
             <div className="regulation-section">
               <h4>Key Compliance Points</h4>
               <ul>
                 {selectedStateInfo.requirements && selectedStateInfo.requirements.length > 0 ? (
                   selectedStateInfo.requirements.map((req, index) => ( <li key={index}>{req}</li> ))
                  ) : ( <li>Specific requirements not listed.</li> )}
                  {selectedStateInfo.hasOwnProperty('foreignEntityRegistration') && ( <li>Foreign Entity Registration Required: {selectedStateInfo.foreignEntityRegistration ? 'Yes' : 'No'}</li> )}
                  {selectedStateInfo.hasOwnProperty('solicitationRequired') && ( <li>Solicitation Registration Required: {selectedStateInfo.solicitationRequired ? 'Yes' : 'No'}</li> )}
                  {selectedStateInfo.hasOwnProperty('registeredAgentRequired') && ( <li>Registered Agent Required: {selectedStateInfo.registeredAgentRequired ? 'Yes' : 'No'}</li> )}
                  {selectedStateInfo.renewalFrequency && ( <li>Renewal Frequency: {selectedStateInfo.renewalFrequency}</li> )}
               </ul>
             </div>
           </div>
           {selectedStateInfo.officialLink && ( <a href={selectedStateInfo.officialLink} target="_blank" rel="noopener noreferrer" className="state-link"> Official State Resources </a> )}
         </div>
       ) : (
          <div className="state-info-panel placeholder">
            {selectedState && !selectedStateInfo ? ( <p>Compliance details not available for {selectedState}.</p> ) : ( <p>Click on a state in the map above to view its compliance requirements.</p> )}
          </div>
       )}
    </>
  );
};

export default StateRegulationsMap;